import { useCallback } from "react";

import { DocumentNode } from "@apollo/client";
import { toast } from "sonner";

import { getPermissionsToUpdate } from "@/components/pieces/roleplays/sharing/permissionTransformers";
import { ShareModalState } from "@/components/pieces/roleplays/sharing/useShareModalState";
import {
  CreateResourcePermissionsInput,
  PermissionLevel,
  ResourceType,
  UpdateResourcePermissionsInput,
  useCreateResourcePermissionsMutation,
  useUpdateResourcePermissionsMutation,
} from "@/graphql";
import { formatErrors } from "@/utils/array";

interface UseResourceMutationsProps<T> {
  resource: T;
  resourceType: ResourceType;
  refetchQuery?: DocumentNode;
  refetchVariables?: Record<string, string>;
  onSuccess: () => void;
  refetchPermissions: () => void;
}

/**
 * Generic hook to manage permission mutations for any resource type
 */
export function useResourceMutations<
  T extends { id: string; visibilityScope?: string | null },
>({
  resource,
  resourceType,
  refetchQuery,
  refetchVariables,
  onSuccess,
  refetchPermissions,
}: UseResourceMutationsProps<T>) {
  const handleError = useCallback((error: Error) => {
    toast.error("Failed to update permissions");
    console.error("Permission update error:", error);
  }, []);

  const refetchQueries =
    refetchQuery && refetchVariables
      ? [{ query: refetchQuery, variables: refetchVariables }]
      : [];

  const [createResourcePermissions, { loading: createLoading }] =
    useCreateResourcePermissionsMutation({
      onCompleted: (response) => {
        if (response?.createResourcePermissions?.ok) {
          toast.success("Permissions updated successfully");
          refetchPermissions();
          onSuccess();
        } else {
          const errorMessage = formatErrors(
            response?.createResourcePermissions?.errors,
          );
          handleError(
            new Error(
              errorMessage || "An error occurred while creating permissions",
            ),
          );
        }
      },
      onError: handleError,
      refetchQueries,
    });

  const [updateResourcePermissions, { loading: updateLoading }] =
    useUpdateResourcePermissionsMutation({
      onCompleted: (response) => {
        if (response?.updateResourcePermissions?.ok) {
          toast.success("Permissions updated successfully");
          refetchPermissions();
          onSuccess();
        } else {
          const errorMessage = formatErrors(
            response?.updateResourcePermissions?.errors,
          );
          handleError(
            new Error(
              errorMessage || "An error occurred while updating permissions",
            ),
          );
        }
      },
      onError: handleError,
      refetchQueries,
    });

  /**
   * Create new permissions for selected users and groups
   */
  const createPermissions = useCallback(
    (state: ShareModalState) => {
      if (!resource?.id) return;

      const emails = state.selectedUsers
        .map((user) => user.email)
        .filter(Boolean);
      const groupIds = state.selectedGroups
        .map((group) => group.id)
        .filter(Boolean);

      const input: CreateResourcePermissionsInput = {
        resourceType,
        resourceId: resource.id,
        permissionLevel: state.selectedPermission as PermissionLevel,
        userEmails: emails,
        groupIds,
        message: state.isNotified ? state.message : null,
        isNotified: state.isNotified,
      };

      createResourcePermissions({ variables: { input } });
    },
    [resource?.id, resourceType, createResourcePermissions],
  );

  /**
   * Update existing permissions
   */
  const updatePermissions = useCallback(
    (state: ShareModalState) => {
      if (!resource?.id) return;

      const { userPermissions, groupPermissions } = getPermissionsToUpdate(
        state.sharedUsers,
        state.sharedGroups,
      );

      // Always include current values in the mutation input
      // The backend will handle determining what actually changed
      const isScenario = resourceType === ResourceType.Scenario;

      const input: UpdateResourcePermissionsInput = {
        resourceType,
        resourceId: resource.id,
        userPermissions:
          userPermissions.length > 0 ? userPermissions : undefined,
        groupPermissions:
          groupPermissions.length > 0 ? groupPermissions : undefined,
        visibilityScope: state.visibilityScope,
        autoShareSessions: isScenario ? state.autoShareSessions : undefined,
      };

      updateResourcePermissions({ variables: { input } });
    },
    [resource?.id, resourceType, updateResourcePermissions],
  );

  return {
    createLoading,
    updateLoading,
    createPermissions,
    updatePermissions,
  };
}
